import { useState, useMemo } from 'react';
import {
    FaChevronLeft,
    FaChevronRight,
    FaFileAlt,
} from 'react-icons/fa';
import { isMarkdownSyntax, renderHTML } from './Constant';
import { minWordEditDistance } from '../utils/wordEditDistance';


export default function TranslationDiffSegmentViewer({
    machineText = "",
    editedText = "",
    isRtl = false,
    onClose,
    loading = false,
    error = "",
    taskId = "",
}) {
    const [viewMode, setViewMode] = useState('segments'); // 'segments' | 'file'

    // ─── Split into segments ───
    const segmentsMachine = useMemo(() => {
        return (machineText || "")
            .split(/\n{2,}/)
            .map(s => s.trim())
            .filter(s => s.length > 0);
    }, [machineText]);

    const segmentsEdited = useMemo(() => {
        return (editedText || "")
            .split(/\n{2,}/)
            .map(s => s.trim())
            .filter(s => s.length > 0);
    }, [editedText]);

    const totalSegments = Math.max(segmentsMachine.length, segmentsEdited.length);

    const [currentIndex, setCurrentIndex] = useState(0);

    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < totalSegments - 1;

    // ─── Per-segment diff (used in both views) ───
    const segmentDiffs = useMemo(() => {
        return segmentsMachine.map((_, idx) => {
            const textM = segmentsMachine[idx] || "";
            const textE = segmentsEdited[idx] || "";

            if (!textM && !textE) {
                return {
                    machineHighlighted: "<p class='text-gray-500'>— no content —</p>",
                    editedHighlighted: "<p class='text-gray-500'>— no content —</p>",
                    summary: { inserted: 0, deleted: 0, replaced: 0, totalChanged: 0 },
                };
            }

            const localDiff = minWordEditDistance(textM, textE);
            const tokensM = textM.split(/(\s+)/);
            const tokensE = textE.split(/(\s+)/);

            let mi = 0, ei = 0;
            const mParts = [], eParts = [];

            localDiff.edits.forEach(edit => {
                while (mi < tokensM.length && ei < tokensE.length && tokensM[mi] === tokensE[ei]) {
                    mParts.push(tokensM[mi]);
                    eParts.push(tokensE[ei]);
                    mi++; ei++;
                }

                if (edit.type === 'delete' && mi < tokensM.length) {
                    const token = tokensM[mi];
                    mParts.push(isMarkdownSyntax(token) || /^\s+$/.test(token) ? token : `<span class="diff-delete">${token}</span>`);
                    mi++;
                } else if (edit.type === 'insert' && ei < tokensE.length) {
                    const token = tokensE[ei];
                    eParts.push(isMarkdownSyntax(token) || /^\s+$/.test(token) ? token : `<span class="diff-insert">${token}</span>`);
                    ei++;
                } else if (edit.type === 'replace' && mi < tokensM.length && ei < tokensE.length) {
                    const tm = tokensM[mi], te = tokensE[ei];
                    mParts.push(isMarkdownSyntax(tm) || /^\s+$/.test(tm) ? tm : `<span class="diff-delete">${tm}</span>`);
                    eParts.push(isMarkdownSyntax(te) || /^\s+$/.test(te) ? te : `<span class="diff-insert">${te}</span>`);
                    mi++; ei++;
                }
            });

            while (mi < tokensM.length) mParts.push(tokensM[mi++]);
            while (ei < tokensE.length) eParts.push(tokensE[ei++]);

            const summary = {
                inserted: localDiff.edits.filter(e => e.type === 'insert').length,
                deleted: localDiff.edits.filter(e => e.type === 'delete').length,
                replaced: localDiff.edits.filter(e => e.type === 'replace').length,
                totalChanged: localDiff.edits.length,
            };

            return {
                machineHighlighted: mParts.join(""),
                editedHighlighted: eParts.join(""),
                summary,
            };
        });
    }, [segmentsMachine, segmentsEdited]);

    // Full file summary
    const fullSummary = useMemo(() => {
        const total = segmentDiffs.reduce((acc, curr) => ({
            inserted: acc.inserted + curr.summary.inserted,
            deleted: acc.deleted + curr.summary.deleted,
            replaced: acc.replaced + curr.summary.replaced,
            totalChanged: acc.totalChanged + curr.summary.totalChanged,
        }), { inserted: 0, deleted: 0, replaced: 0, totalChanged: 0 });

        return total;
    }, [segmentDiffs]);

    const goPrev = () => hasPrev && setCurrentIndex(i => i - 1);
    const goNext = () => hasNext && setCurrentIndex(i => i + 1);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[95vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                
                {/* Header */}
                <div className="px-6 py-4 border-b bg-gray-100 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <h2 className="text-xl font-semibold text-gray-800">
                            Translation Diff - <span className="text-blue-600">{taskId || "Task"}</span>
                        </h2>

                        <div className="flex border rounded-lg bg-white text-sm font-medium">
                            <button
                                onClick={() => setViewMode('segments')}
                                className={`px-5 py-2 rounded-l-lg transition ${viewMode === 'segments' ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100 text-gray-700'}`}
                            >
                                Segment View
                            </button>
                            <button
                                onClick={() => setViewMode('file')}
                                className={`px-5 py-2 rounded-r-lg transition ${viewMode === 'file' ? 'bg-indigo-600 text-white' : 'hover:bg-gray-100 text-gray-700'}`}
                            >
                                File Difference
                            </button>
                        </div>
                    </div>

                    <button onClick={onClose} className="w-12 h-12 flex items-center justify-center text-3xl text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-full transition">
                        ✕
                    </button>
                </div>

                {/* Summary */}
                <div className="px-6 py-3 bg-gray-50 border-b text-sm flex gap-6">
                    <span>File Level Changes: <strong>{fullSummary.totalChanged}</strong></span>
                    <span className="text-green-700">+ Added: {fullSummary.inserted}</span>
                    <span className="text-red-700">- Removed: {fullSummary.deleted}</span>
                    <span className="text-amber-700">↔ Replaced: {fullSummary.replaced}</span>
                </div>

                {/* Body */}
                <div className="flex-1 p-6 overflow-hidden bg-gray-50 flex flex-col">
                    {loading ? (
                        <div className="flex items-center justify-center h-full text-gray-500 text-lg">Computing diff...</div>
                    ) : error ? (
                        <div className="flex items-center justify-center h-full text-red-600 text-lg">{error}</div>
                    ) : viewMode === 'segments' ? (
                        // Segment View (Single Segment Navigation)
                        totalSegments === 0 ? (
                            <div className="flex items-center justify-center h-full text-gray-500">No content to compare</div>
                        ) : (
                            <div className="flex flex-col h-full">
                                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-6 overflow-hidden">
                                    <div className="flex flex-col h-full border rounded-xl overflow-hidden bg-white shadow-sm">
                                        <div className="bg-orange-50 px-5 py-3 font-medium text-orange-800 border-b">
                                            Machine Translation - Segment {currentIndex + 1}
                                        </div>
                                        <div className={`flex-1 p-6 overflow-auto prose max-w-none ${isRtl ? "urdu-font text-right" : ""}`}
                                            dir={isRtl ? "rtl" : "ltr"}
                                            style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                            dangerouslySetInnerHTML={renderHTML(segmentDiffs[currentIndex]?.machineHighlighted)} />
                                    </div>

                                    <div className="flex flex-col h-full border rounded-xl overflow-hidden bg-white shadow-sm">
                                        <div className="bg-emerald-50 px-5 py-3 font-medium text-emerald-800 border-b">
                                            Human Edited - Segment {currentIndex + 1}
                                        </div>
                                        <div className={`flex-1 p-6 overflow-auto prose max-w-none ${isRtl ? "urdu-font text-right" : ""}`}
                                            dir={isRtl ? "rtl" : "ltr"}
                                            style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                            dangerouslySetInnerHTML={renderHTML(segmentDiffs[currentIndex]?.editedHighlighted)} />
                                    </div>
                                </div>

                                {/* Navigation */}
                                <div className="mt-4 px-4 py-3 bg-white border rounded-xl shadow-sm flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <FaFileAlt className="text-indigo-600 text-xl" />
                                        <span>Segment <strong className="text-indigo-700">{currentIndex + 1}</strong> of {totalSegments}</span>
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            onClick={goPrev}
                                            disabled={!hasPrev}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition ${hasPrev ? "bg-indigo-50 text-indigo-700 hover:bg-indigo-100" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
                                        >
                                            <FaChevronLeft /> Prev
                                        </button>
                                        <button
                                            onClick={goNext}
                                            disabled={!hasNext}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition ${hasNext ? "bg-indigo-50 text-indigo-700 hover:bg-indigo-100" : "bg-gray-100 text-gray-400 cursor-not-allowed"}`}
                                        >
                                            Next <FaChevronRight />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )
                    ) : (
                        // ─── FILE DIFFERENCE VIEW - SEGMENT WISE TABLE ───
                        <div className="flex-1 flex flex-col overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
                            <div className="grid grid-cols-2 border-b bg-gray-50 sticky top-0 z-10">
                                <div className="px-6 py-4 font-semibold text-orange-800 border-r">Machine Translation</div>
                                <div className="px-6 py-4 font-semibold text-emerald-800">Human Edited</div>
                            </div>

                            <div className="flex-1 overflow-auto">
                                <table className="w-full border-collapse">
                                    <tbody>
                                        {segmentsMachine.map((_, idx) => (
                                            <tr key={idx} className="border-b last:border-b-0 hover:bg-gray-50">
                                                <td className="border-r border-gray-200 p-5 align-top bg-gray-50/30">
                                                    {/* <div className="text-xs text-gray-500 mb-2 font-medium">
                                                        Segment {idx + 1}
                                                    </div> */}
                                                    <div
                                                        className={`prose max-w-none leading-relaxed ${isRtl ? "urdu-font text-right" : ""}`}
                                                        dir={isRtl ? "rtl" : "ltr"}
                                                        style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                                        dangerouslySetInnerHTML={renderHTML(segmentDiffs[idx]?.machineHighlighted)}
                                                    />
                                                </td>
                                                <td className="p-5 align-top">
                                                    {/* <div className="text-xs text-gray-500 mb-2 font-medium">
                                                        Segment {idx + 1}
                                                    </div> */}
                                                    <div
                                                        className={`prose max-w-none leading-relaxed ${isRtl ? "urdu-font text-right" : ""}`}
                                                        dir={isRtl ? "rtl" : "ltr"}
                                                        style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                                                        dangerouslySetInnerHTML={renderHTML(segmentDiffs[idx]?.editedHighlighted)}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}